/**
 * Έκπτωση και κέρασμα ΓΡΑΜΜΩΝ στο καλάθι τραπεζιού του ταμείου (απόφαση ιδιοκτήτη 6/10/2026:
 * λογοδοσία προσωπικού — φεύγουν οι σταθερές εκπτώσεις όλης της παραγγελίας· ο ταμίας επιλέγει
 * γραμμές και πατά «Έκπτωση» (% ή €) ή «Κέρασμα»).
 *
 * Καθαρές συναρτήσεις, χωρίς React — ό,τι αφορά ποσά μένει εδώ, με τεστ (dineInBasketDiscount.test.ts).
 *
 * Κανόνες χρημάτων:
 *  - πάντα από την ΠΛΗΡΗ τιμή μονάδας (`full_unit_price` = πριν από την ενέργεια του ταμία): μια νέα
 *    έκπτωση/κέρασμα ΑΝΤΙΚΑΘΙΣΤΑ την προηγούμενη της γραμμής, ποτέ δεν προστίθεται·
 *  - κάθε τιμή μονάδας σε ακέραια λεπτά·
 *  - %: τιμή μονάδας = πλήρης × (1 − p/100), στρογγυλεμένη στο λεπτό με το μισό προς τα πάνω, σε
 *    ακέραια λεπτά (όχι με float: 2,35 € −10% = 2,115 → 2,12, ενώ το float δίνει 2,11)·
 *  - €: το ποσό μοιράζεται στις γραμμές ανάλογα με την αξία τους, ΑΚΡΙΒΩΣ στο λεπτό (Σ γραμμών =
 *    αξία − ποσό). Όταν δεν βγαίνει με την ίδια τιμή σε όλα τα τεμάχια μιας γραμμής (π.χ. 2 τεμάχια
 *    και μονός αριθμός λεπτών), ΕΝΑ τεμάχιο γίνεται χωριστή γραμμή με δική του τιμή (σε ακραίες
 *    περιπτώσεις — έκπτωση σχεδόν 100% — λίγα τεμάχια με 1 λεπτό διαφορά). Ανώτατο: η αξία των
 *    γραμμών (≤ 100%)·
 *  - γραμμή με αυτόματη προσφορά δεν παίρνει έκπτωση (κρατά την τιμή της προσφοράς)· κέρασμα
 *    παίρνει, με αξία την τιμή που θα πλήρωνε ο πελάτης (της προσφοράς).
 *
 * Τα πεδία τιμής κάθε γραμμής προς τον server (POST …/sessions/:id/orders): basketLinePricing().
 */

export type BasketDiscountKind = 'percent' | 'amount';

/** Ό,τι χρειάζεται από μια γραμμή καλαθιού· οι υπόλοιπες ιδιότητές της περνούν αυτούσιες. */
export interface AdjustableBasketLine {
  quantity: number;
  unit_price: number;
  /** Τιμή μονάδας ΠΡΙΝ από την έκπτωση/το κέρασμα του ταμία (με την προσφορά, αν έχει).
   *  Υπάρχει μόνο σε γραμμή με ενέργεια ταμία — αλλιώς πλήρης τιμή = unit_price. */
  full_unit_price?: number | null;
  is_treat?: boolean;
  /** Πώς δόθηκε η έκπτωση — για την ετικέτα της γραμμής («−20%» ή «−5,00 €»). */
  discount_kind?: BasketDiscountKind | null;
  /** Το % της έκπτωσης· στο € το πραγματικό % της γραμμής, με 2 δεκαδικά. */
  discount_percent?: number | null;
  /** Ο προαιρετικός λόγος του ταμία (της έκπτωσης ή του κεράσματος). */
  adjust_reason?: string | null;
  applied_promotion_id?: number | null;
  /** Προσφορά: η τιμή ΠΡΙΝ από την προσφορά. */
  original_unit_price?: number | null;
}

/** Ποσό σε ακέραια λεπτά — ανεκτικό στον θόρυβο των float (9,8000000001 → 980). */
export const toCents = (amount: unknown): number => {
  const n = Number(amount);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

const fromCents = (cents: number): number => cents / 100;

const qtyOf = (line: { quantity: number }): number => {
  const q = Math.round(Number(line.quantity));
  return Number.isFinite(q) && q > 0 ? q : 0;
};

/** Ο λόγος όπως φεύγει: χωρίς περιττά κενά, ως 200 χαρακτήρες (όσο κρατά ο server)· κενός → null. */
export const cleanReason = (reason: string | null | undefined): string | null => {
  if (typeof reason !== 'string') return null;
  const clean = reason.replace(/\s+/g, ' ').trim().slice(0, 200);
  return clean || null;
};

/** Η γραμμή έχει έκπτωση ή κέρασμα ταμία. */
export const hasCashierAdjustment = (line: AdjustableBasketLine): boolean =>
  line.is_treat === true || line.full_unit_price != null;

/** Γραμμή με αυτόματη προσφορά — ίδιος έλεγχος με την αναίρεση προσφοράς του καλαθιού. */
export const isPromotionLine = (line: AdjustableBasketLine): boolean =>
  !!line.applied_promotion_id && line.original_unit_price != null;

/** Τιμή μονάδας χωρίς την ενέργεια του ταμία. */
export const fullUnitPrice = (line: AdjustableBasketLine): number =>
  line.full_unit_price != null ? Number(line.full_unit_price) || 0 : Number(line.unit_price) || 0;

/** Αξία γραμμής στην πλήρη τιμή, σε λεπτά. */
export const lineFullCents = (line: AdjustableBasketLine): number => toCents(fullUnitPrice(line)) * qtyOf(line);

/** Αξία γραμμής όπως χρεώνεται, σε λεπτά. */
export const lineCents = (line: AdjustableBasketLine): number => toCents(line.unit_price) * qtyOf(line);

/** Η έκπτωση ή το κέρασμα της γραμμής σε λεπτά (0 χωρίς ενέργεια ταμία). */
export const lineReductionCents = (line: AdjustableBasketLine): number =>
  hasCashierAdjustment(line) ? Math.max(0, lineFullCents(line) - lineCents(line)) : 0;

/** «5,00 €» από λεπτά. */
export const formatEuroGr = (cents: number): string => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

/** «20», «12,5» — το % για την ετικέτα. */
export const formatPercentGr = (percent: number | null | undefined): string => {
  const p = Math.round((Number(percent) || 0) * 100) / 100;
  return String(p).replace('.', ',');
};

/** Τιμή μονάδας μετά από p% έκπτωση: στρογγύλευση στο λεπτό (μισό προς τα πάνω), σε ακέραια λεπτά. */
export function percentUnitCents(fullCents: number, percent: number): number {
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  const hundredths = Math.round(p * 100); // 12,5% → 1250: ακέραιος αριθμητής, ακριβές «μισό»
  return Math.round((fullCents * (10000 - hundredths)) / 10000);
}

/** Το πραγματικό % μιας έκπτωσης, με 2 δεκαδικά. */
export const effectivePercent = (fullCents: number, unitCents: number): number =>
  fullCents > 0 ? Math.round(((fullCents - unitCents) * 10000) / fullCents) / 100 : 0;

/** Η γραμμή χωρίς έκπτωση/κέρασμα ταμία: πλήρης τιμή, κανένα πεδίο ενέργειας. */
export function withoutAdjustment<T extends AdjustableBasketLine>(line: T): T {
  if (!hasCashierAdjustment(line)) return line;
  const {
    full_unit_price: _full, is_treat: _treat, discount_kind: _kind, discount_percent: _percent, adjust_reason: _reason,
    ...rest
  } = line;
  return { ...rest, unit_price: fromCents(toCents(fullUnitPrice(line))) } as T;
}

/** Γραμμή (ΧΩΡΙΣ ενέργεια, άρα unit_price = πλήρης) με τιμή μονάδας `unitCents`. Καμία μείωση → ως έχει. */
function discountedLine<T extends AdjustableBasketLine>(
  base: T,
  unitCents: number,
  kind: BasketDiscountKind,
  percent: number | null,
  reason: string | null
): T {
  const fullCents = toCents(base.unit_price);
  if (unitCents >= fullCents) return base;
  return {
    ...base,
    full_unit_price: fromCents(fullCents),
    unit_price: fromCents(Math.max(0, unitCents)),
    discount_kind: kind,
    discount_percent: percent ?? effectivePercent(fullCents, unitCents),
    adjust_reason: reason,
  };
}

/** Κέρασμα στις επιλεγμένες γραμμές (θέσεις στο καλάθι): ολόκληρες οι γραμμές, τιμή 0. */
export function applyTreat<T extends AdjustableBasketLine>(
  lines: T[],
  selected: readonly number[],
  reason?: string | null
): T[] {
  const pick = new Set(selected);
  const why = cleanReason(reason);
  return lines.map((line, index) => {
    if (!pick.has(index)) return line;
    const base = withoutAdjustment(line);
    return {
      ...base,
      full_unit_price: fromCents(toCents(base.unit_price)),
      unit_price: 0,
      is_treat: true,
      adjust_reason: why,
    };
  });
}

/** Έκπτωση p% στις επιλεγμένες γραμμές· γραμμές με προσφορά μένουν όπως είναι. */
export function applyPercentDiscount<T extends AdjustableBasketLine>(
  lines: T[],
  selected: readonly number[],
  percent: number,
  reason?: string | null
): T[] {
  const pick = new Set(selected);
  const why = cleanReason(reason);
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  return lines.map((line, index) => {
    if (!pick.has(index) || isPromotionLine(line)) return line;
    const base = withoutAdjustment(line);
    return discountedLine(base, percentUnitCents(toCents(base.unit_price), p), 'percent', p, why);
  });
}

export interface AmountDiscountResult<T> {
  lines: T[];
  /** Ό,τι πράγματι αφαιρέθηκε, σε λεπτά (≤ αξία των γραμμών). */
  appliedCents: number;
  /** Το ποσό ξεπερνούσε την αξία των γραμμών και περιορίστηκε σε αυτήν. */
  capped: boolean;
  /** Τεμάχιο (ή τεμάχια) έγινε χωριστή γραμμή για να βγει το ποσό ακριβώς. */
  split: boolean;
}

/**
 * Έκπτωση ποσού (€) στις επιλεγμένες γραμμές, ΑΚΡΙΒΩΣ στο λεπτό:
 *  1. κάθε τεμάχιο παίρνει το αναλογικό του κομμάτι στρογγυλεμένο προς τα κάτω (λεπτό ανά μονάδα)·
 *  2. τα λεπτά που περισσεύουν μοιράζονται +1 λεπτό ανά τεμάχιο σε ΟΛΟΚΛΗΡΕΣ γραμμές, πρώτα σε όσες
 *     έχασαν περισσότερο στη στρογγύλευση·
 *  3. ό,τι μένει (λιγότερο από τα τεμάχια κάθε γραμμής που χωρά) το παίρνει ΕΝΑ τεμάχιο σε χωριστή
 *     γραμμή· αν κανένα τεμάχιο δεν χωρά όλο το υπόλοιπο, τόσα τεμάχια όσα τα λεπτά, με 1 λεπτό το καθένα.
 * Γραμμές με προσφορά μένουν όπως είναι και δεν μετρούν στην αξία.
 */
export function applyAmountDiscount<T extends AdjustableBasketLine>(
  lines: T[],
  selected: readonly number[],
  amount: number,
  reason?: string | null
): AmountDiscountResult<T> {
  const pick = new Set(selected);
  const why = cleanReason(reason);
  const targets = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line, index }) => pick.has(index) && !isPromotionLine(line))
    .map(({ line, index }) => {
      const base = withoutAdjustment(line);
      return { index, base, unit: toCents(base.unit_price), qty: qtyOf(base), cut: 0 };
    });

  const gross = targets.reduce((s, t) => s + t.unit * t.qty, 0);
  const requested = Math.max(0, toCents(amount));
  const total = Math.min(requested, gross);

  if (total > 0) {
    // 1. αναλογικά, προς τα κάτω, ανά τεμάχιο
    targets.forEach(t => { t.cut = Math.floor((total * t.unit) / gross); });
    let rest = total - targets.reduce((s, t) => s + t.cut * t.qty, 0);

    // 2. +1 λεπτό ανά τεμάχιο σε ολόκληρες γραμμές
    const order = targets.slice().sort((a, b) =>
      ((total * b.unit) % gross) - ((total * a.unit) % gross) ||
      b.unit * b.qty - a.unit * a.qty ||
      a.index - b.index
    );
    let progressed = true;
    while (rest > 0 && progressed) {
      progressed = false;
      for (let k = 0; k < order.length; k++) {
        const t = order[k];
        if (t.qty > 0 && t.qty <= rest && t.cut + 1 <= t.unit) {
          t.cut += 1;
          rest -= t.qty;
          progressed = true;
        }
      }
    }

    // 3. το υπόλοιπο σε χωριστή γραμμή (ένα τεμάχιο, αλλιώς τόσα τεμάχια όσα τα λεπτά)
    let splitOf: { index: number; units: number; cut: number } | null = null;
    if (rest > 0) {
      const one = order.find(t => t.qty >= 2 && t.unit - t.cut >= rest);
      if (one) {
        splitOf = { index: one.index, units: 1, cut: one.cut + rest };
      } else {
        const few = order.find(t => t.qty > rest && t.unit - t.cut >= 1);
        if (few) splitOf = { index: few.index, units: rest, cut: few.cut + 1 };
      }
      // Αδύνατο κατά κατασκευή (τα λεπτά που μένουν < τεμάχια κάθε γραμμής με περιθώριο)· αν
      // συμβεί, λιγότερη έκπτωση, ποτέ περισσότερη.
      if (splitOf) rest = 0;
    }

    const byIndex = new Map(targets.map(t => [t.index, t] as [number, typeof t]));
    const out: T[] = [];
    lines.forEach((line, index) => {
      const t = byIndex.get(index);
      if (!t) {
        out.push(line);
        return;
      }
      if (splitOf && splitOf.index === index) {
        out.push(discountedLine({ ...t.base, quantity: t.qty - splitOf.units }, t.unit - t.cut, 'amount', null, why));
        out.push(discountedLine({ ...t.base, quantity: splitOf.units }, t.unit - splitOf.cut, 'amount', null, why));
        return;
      }
      out.push(discountedLine(t.base, t.unit - t.cut, 'amount', null, why));
    });
    return { lines: out, appliedCents: total - rest, capped: requested > gross, split: !!splitOf };
  }

  // Τίποτα να αφαιρεθεί: οι γραμμές στην πλήρη τιμή τους (η νέα ενέργεια αντικαθιστά την παλιά).
  const cleared = lines.map((line, index) =>
    (pick.has(index) && !isPromotionLine(line) ? withoutAdjustment(line) : line));
  return { lines: cleared, appliedCents: 0, capped: requested > gross, split: false };
}

/** «Αφαίρεση»: οι επιλεγμένες γραμμές ξανά στην πλήρη τιμή τους. */
export function removeAdjustments<T extends AdjustableBasketLine>(lines: T[], selected: readonly number[]): T[] {
  const pick = new Set(selected);
  return lines.map((line, index) => (pick.has(index) ? withoutAdjustment(line) : line));
}

/** Τα πεδία τιμής μιας γραμμής στο POST …/sessions/:id/orders (σύμβαση 6/10/2026). */
export interface BasketLinePricing {
  unit_price: number;
  original_unit_price: number | null;
  applied_promotion_id: number | null;
  discount_percent?: number | null;
  /** Η έκπτωση ΟΛΗΣ της γραμμής σε € = (πλήρης − τιμή) × ποσότητα. */
  discount_amount?: number | null;
  is_treat?: true;
  treat_reason?: string;
  discount_reason?: string;
}

/**
 * Κέρασμα: is_treat, τιμή 0, πλήρης τιμή στο original_unit_price, χωρίς πεδία έκπτωσης.
 * Έκπτωση: τιμή μετά την έκπτωση, πλήρης στο original_unit_price, %, € ΓΡΑΜΜΗΣ.
 * Κανονική γραμμή: ακριβώς ό,τι έφευγε πριν (και η προσφορά με το original_unit_price της).
 * Κέρασμα/έκπτωση ταμία δεν στέλνει ποτέ προσφορά — η αξία τους δεν είναι έκπτωση προσφοράς.
 */
export function basketLinePricing(line: AdjustableBasketLine): BasketLinePricing {
  const reason = cleanReason(line.adjust_reason);
  if (line.is_treat === true) {
    return {
      is_treat: true,
      unit_price: 0,
      original_unit_price: fromCents(toCents(fullUnitPrice(line))),
      applied_promotion_id: null,
      ...(reason ? { treat_reason: reason } : {}),
    };
  }
  if (line.full_unit_price != null) {
    const fullCents = toCents(line.full_unit_price);
    const unitCents = toCents(line.unit_price);
    return {
      unit_price: fromCents(unitCents),
      original_unit_price: fromCents(fullCents),
      discount_percent: line.discount_percent ?? effectivePercent(fullCents, unitCents),
      discount_amount: fromCents((fullCents - unitCents) * qtyOf(line)),
      applied_promotion_id: null,
      ...(reason ? { discount_reason: reason } : {}),
    };
  }
  return {
    unit_price: line.unit_price,
    discount_percent: null,
    discount_amount: null,
    applied_promotion_id: line.applied_promotion_id || null,
    original_unit_price: line.original_unit_price ?? null,
  };
}
